import React, { FC, PropsWithChildren } from 'react';
import { notchTop, scale } from '@components/frame';
import { Text, View } from 'react-native';
import ContextHeader from '@v2/pages/common/components/ContextHeader/ContextHeader';
import { font } from '@styles/fonts';
import ColorWrapper, { WithColor } from '@styles/colors';
import BackButton from '@components/common/backButton';
import {
  PHONE_DETAILS_HEADER_MARGIN_TOP,
  SCREEN_PADDING_HORIZONTAL_FOR_ITEM,
  SCREEN_PADDING_HORIZONTAL_FOR_PAGE,
  SCREEN_PADDING_VERTICAL_FOR_HEADER,
  TEST_IDS,
} from '../../constants/base.constants';
import { isPhone, isWeb } from '@helpers';
import i18n from 'i18n-js';
import DetailsPageModals from '@v2/pages/common/modals/DetailsPageModals';
import MoreButton from '@components/common/moreButton';
import CloseButton from '@components/closeButton';
import SendToImnoo from '@v2/pages/productionOrder/modules/productionOrder/components/SendToImnoo';
import Buttons from '@styles/buttons';
import AddButtonWithHover from '@components/operatorPanel/components/elements/buttons/AddButtonWithHover';
import { useManageDetailsPageWrapper } from './hooks/useManageDetailsPageWrapper';

export interface DetailsPageWrapperProps extends PropsWithChildren, WithColor {
  title: string;
  shouldUsePhoneLayoutForTitle?: boolean;
}

const DetailsPageWrapper: FC<DetailsPageWrapperProps> = ({ title, children, C, shouldUsePhoneLayoutForTitle = false }) => {
  const {
    actionsModalOpen,
    setActionsModalOpen,
    currentActiveTab,
    shouldDisplayBackButton,
    activeTabTitle,
    prevTabTitle,
    shouldDisplayModalTriggerButton,
    shouldDisplayImnooButton,
    shouldDisplayDoneButton,
    shouldDisplayAddButton,
    onBackButtonPress,
    onClose,
    onDoneEditingButtonPress,
    onUploadNewVersion,
  } = useManageDetailsPageWrapper({ title });

  const BackButtonComponent = (
    <View style={{ opacity: shouldDisplayBackButton ? 1 : 0, pointerEvents: shouldDisplayBackButton ? 'auto' : 'none', alignItems: 'flex-start' }}>
      <BackButton
        {...{
          text: prevTabTitle,
          withoutText: isPhone || shouldUsePhoneLayoutForTitle,
          style: isPhone || shouldUsePhoneLayoutForTitle ? { paddingLeft: 0, left: scale(-5), top: scale(0.5) } : {},
          onPress: onBackButtonPress,
          testID: TEST_IDS.detailsPage.backButton,
          textTestID: TEST_IDS.detailsPage.backButtonText,
        }}
      />
    </View>
  );

  const ActionsButtonComponent = (
    <View
      style={{
        gap: scale(13),
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        marginRight: scale(isPhone ? -2 : 0),
      }}
    >
      {shouldDisplayDoneButton && (
        <Buttons {...{ variant: 'M', testID: TEST_IDS.detailsPage.doneButton, onPress: onDoneEditingButtonPress, borderRadius: scale(14.5), loading: false }}>
          {i18n.t('Done')}
        </Buttons>
      )}
      {shouldDisplayAddButton && <AddButtonWithHover C={C} scale={scale} onPress={onUploadNewVersion} iconColor="Button/Light/Primary" />}
      {shouldDisplayModalTriggerButton && <MoreButton C={C} testID={TEST_IDS.detailsPage.moreButton} onPress={() => setActionsModalOpen(true)} />}
      {shouldDisplayImnooButton && <SendToImnoo />}
      <CloseButton C={C} testID={TEST_IDS.detailsPage.closeButton} onPress={onClose} />
    </View>
  );

  return (
    <View
      style={{
        paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL_FOR_PAGE),
        position: 'relative',
        backgroundColor: C(isWeb ? 'Background/Light/System' : 'Background/Light/Panel'),
        flex: 1,
      }}
    >
      <View
        style={[
          {
            flexDirection: 'row',
            alignItems: 'center',
            marginTop: scale(isPhone ? notchTop + PHONE_DETAILS_HEADER_MARGIN_TOP : 41),
            marginBottom: scale(18),
            height: scale(27),
          },
          !isPhone && { paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL_FOR_ITEM) },
        ]}
      >
        {isPhone || shouldUsePhoneLayoutForTitle ? (
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flex: 1 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(15), flex: 1, justifyContent: 'flex-start' }}>
              {shouldDisplayBackButton && BackButtonComponent}
              <Text
                numberOfLines={1}
                style={[
                  font[isPhone ? 'Headline' : 'Title 2'],
                  { color: C('Label/Light/Primary'), textAlign: 'left', paddingRight: scale(shouldDisplayBackButton ? 30 : 0) },
                ]}
              >
                {activeTabTitle}
              </Text>
            </View>
            {ActionsButtonComponent}
          </View>
        ) : (
          <View style={{ flexDirection: 'row', alignItems: 'center', width: '100%' }}>
            <View style={{ flex: 1 }}>{BackButtonComponent}</View>

            <View style={{ flex: 1, alignItems: 'center' }}>
              <Text numberOfLines={1} style={[font['Title 2'], { color: C('Label/Light/Primary'), textAlign: 'center' }]}>
                {activeTabTitle}
              </Text>
            </View>

            <View style={{ flex: 1, alignItems: 'flex-end' }}>{ActionsButtonComponent}</View>
          </View>
        )}
      </View>

      <ContextHeader panelViewMode={false} />

      <View style={{ marginTop: scale(SCREEN_PADDING_VERTICAL_FOR_HEADER), flex: 1 }}>{children}</View>

      <DetailsPageModals
        {...{
          C,
          panelViewMode: true,
          actionsModalOpen,
          setActionsModalOpen,
          modalType: currentActiveTab?.modalType,
        }}
      />
    </View>
  );
};

export default ColorWrapper(DetailsPageWrapper);
